import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import PurchaseList from "@/pages/PurchaseList";
import PurchaseDetail from "@/pages/PurchaseDetail";
import PaymentRequests from "@/pages/PaymentRequests";
import SalesList from "@/pages/SalesList";
import SalesDetail from "@/pages/SalesDetail";
import BankUpload from "@/pages/BankUpload";
import Ledgers from "@/pages/Ledgers";
import LedgerDetail from "@/pages/LedgerDetail";
import Users from "@/pages/Users";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Advances from "@/pages/Advances";
import CashVouchers from "@/pages/CashVouchers";
import Fiat from "@/pages/Fiat";
import AuditLog from "@/pages/AuditLog";
import { Payables, Receivables, Expenses } from "@/pages/Reports";
import GstSetOff from "@/pages/GstSetOff";
import Gstr2bRecon from "@/pages/Gstr2bRecon";
import PgRecon from "@/pages/PgRecon";
import AgedReceivables from "@/pages/AgedReceivables";
import Settings from "@/pages/Settings";
import LoansList from "@/pages/LoansList";
import LoanDetail from "@/pages/LoanDetail";
import PettyCash from "@/pages/PettyCash";
import CashBankPosition from "@/pages/CashBankPosition";
import Employees from "@/pages/Employees";
import EmployeeAdvances from "@/pages/EmployeeAdvances";
import Payroll from "@/pages/Payroll";
import PayrollApprovals from "@/pages/PayrollApprovals";
import AllApprovals from "@/pages/AllApprovals";
import PendingPayouts from "@/pages/PendingPayouts";
import BulkImports from "@/pages/BulkImports";
import PerformanceReviews from "@/pages/PerformanceReviews";
import SalaryReconcile from "@/pages/SalaryReconcile";
import Stock from "@/pages/Stock";
import MonthlyPnL from "@/pages/MonthlyPnL";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import ContractWorkers from "@/pages/ContractWorkers";
import ContractPayouts from "@/pages/ContractPayouts";
import Leaves from "@/pages/Leaves";
import MyPayslips from "@/pages/MyPayslips";
import NetWorth from "@/pages/NetWorth";
import Biometric from "@/pages/Biometric";
import AskFinance from "@/pages/AskFinance";
import ExpenseClaims from "@/pages/ExpenseClaims";
import ReportsCA from "@/pages/ReportsCA";
import RecurringExpenses from "@/pages/RecurringExpenses";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

// Non-founders land on a role-appropriate first screen instead of the founder dashboard.
const HOME_BY_ROLE = {
    founder: null, // renders the actual Dashboard
    accountant: "/ledgers",
    purchase: "/purchase",
    dispatch: "/sales",
    hr: "/employees",
    production: "/petty-cash",
    sales_staff: "/petty-cash",
    customer_experience: "/petty-cash",
    employee: "/my-advances",
};

function RoleHome() {
    const { user } = useAuth();
    const dest = HOME_BY_ROLE[user?.role];
    if (dest) return <Navigate to={dest} replace />;
    return <Dashboard />;
}

const withLayout = (el, roles) => (
    <ProtectedRoute roles={roles}>
        <Layout>{el}</Layout>
    </ProtectedRoute>
);

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={withLayout(<RoleHome />)} />
                    <Route path="/purchase" element={withLayout(<PurchaseList />, ["founder", "accountant", "purchase"])} />
                    <Route path="/purchase/:id" element={withLayout(<PurchaseDetail />, ["founder", "accountant", "purchase"])} />
                    <Route path="/payment-requests" element={withLayout(<PaymentRequests />, ["founder", "accountant", "purchase"])} />
                    <Route path="/sales" element={withLayout(<SalesList />, ["founder", "accountant", "dispatch"])} />
                    <Route path="/sales/:id" element={withLayout(<SalesDetail />, ["founder", "accountant", "dispatch"])} />
                    <Route path="/advances" element={withLayout(<Advances />, ["founder", "accountant", "dispatch"])} />
                    <Route path="/bank" element={withLayout(<BankUpload />, ["founder", "accountant"])} />
                    <Route path="/cash" element={withLayout(<CashVouchers />, ["founder", "accountant"])} />
                    <Route path="/fiat" element={withLayout(<Fiat />, ["founder", "accountant", "purchase", "sales_staff", "dispatch"])} />
                    <Route path="/ledgers" element={withLayout(<Ledgers />, ["founder", "accountant", "purchase", "dispatch"])} />
                    <Route path="/ledgers/:id" element={withLayout(<LedgerDetail />, ["founder", "accountant", "purchase", "dispatch"])} />
                    <Route path="/reports/payables" element={withLayout(<Payables />, ["founder", "accountant", "purchase"])} />
                    <Route path="/reports/receivables" element={withLayout(<Receivables />, ["founder", "accountant", "dispatch"])} />
                    <Route path="/reports/expenses" element={withLayout(<Expenses />, ["founder", "accountant"])} />
                    <Route path="/reports/gst" element={withLayout(<GstSetOff />, ["founder", "accountant"])} />
                    <Route path="/reports/pg-recon" element={withLayout(<PgRecon />, ["founder", "accountant"])} />
                    <Route path="/reports/gstr-2b" element={withLayout(<Gstr2bRecon />, ["founder", "accountant"])} />
                    <Route path="/reports/aged-receivables" element={withLayout(<AgedReceivables />, ["founder", "accountant", "dispatch"])} />
                    <Route path="/chart-of-accounts" element={withLayout(<ChartOfAccounts />, ["founder", "accountant"])} />
                    <Route path="/users" element={withLayout(<Users />, ["founder"])} />
                    <Route path="/audit-log" element={withLayout(<AuditLog />, ["founder", "accountant"])} />
                    <Route path="/settings" element={withLayout(<Settings />, ["founder", "accountant"])} />
                    <Route path="/loans" element={withLayout(<LoansList />, ["founder"])} />
                    <Route path="/loans/:id" element={withLayout(<LoanDetail />, ["founder"])} />
                    <Route path="/petty-cash" element={withLayout(<PettyCash />)} />
                    <Route path="/cash-bank" element={withLayout(<CashBankPosition />, ["founder", "accountant"])} />
                    <Route path="/employees" element={withLayout(<Employees />, ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"])} />
                    <Route path="/employee-advances" element={withLayout(<EmployeeAdvances />, ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience", "employee"])} />
                    <Route path="/payroll" element={withLayout(<Payroll />, ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"])} />
                    <Route path="/payroll-approvals" element={withLayout(<PayrollApprovals />, ["founder", "hr"])} />
                    <Route path="/all-approvals" element={withLayout(<AllApprovals />, ["founder", "accountant"])} />
                    <Route path="/pending-payouts" element={withLayout(<PendingPayouts />, ["founder", "accountant"])} />
                    <Route path="/bulk-imports" element={withLayout(<BulkImports />, ["founder"])} />
                    <Route path="/performance-reviews" element={withLayout(<PerformanceReviews />, ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"])} />
                    <Route path="/salary-reconcile" element={withLayout(<SalaryReconcile />, ["founder", "accountant"])} />
                    <Route path="/stock" element={withLayout(<Stock />, ["founder", "accountant"])} />
                    <Route path="/reports/monthly-pnl" element={withLayout(<MonthlyPnL />, ["founder"])} />
                    <Route path="/assets" element={withLayout(<Assets />, ["founder"])} />
                    <Route path="/assets/:id" element={withLayout(<AssetDetail />, ["founder"])} />
                    <Route path="/contract-workers" element={withLayout(<ContractWorkers />, ["founder", "accountant", "hr", "production", "dispatch"])} />
                    <Route path="/contract-payouts" element={withLayout(<ContractPayouts />, ["founder", "accountant", "hr", "production", "dispatch"])} />
                    <Route path="/leaves" element={withLayout(<Leaves />)} />
                    <Route path="/my-payslips" element={withLayout(<MyPayslips />, ["founder"])} />
                    <Route path="/reports/networth" element={withLayout(<NetWorth />, ["founder", "accountant"])} />
                    <Route path="/biometric" element={withLayout(<Biometric />, ["founder", "accountant", "hr", "production", "dispatch", "purchase", "sales_staff", "customer_experience"])} />
                    <Route path="/ask" element={withLayout(<AskFinance />, ["founder"])} />
                    <Route path="/expense-claims" element={withLayout(<ExpenseClaims />)} />
                    <Route path="/reports-ca" element={withLayout(<ReportsCA />, ["founder", "accountant"])} />
                    <Route path="/recurring-expenses" element={withLayout(<RecurringExpenses />, ["founder", "accountant"])} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
